import { Link, useMatch } from "react-router-dom";

import { signOut, useUser } from "../auth/auth-client";
import { MenuSheet } from "./MenuSheet";
import {
  FOOTER_LINKS,
  type FooterLink,
  type NavTab,
  useMobileBottomNavTabs,
  useNavTabs,
} from "./nav";
import { UserAvatar } from "@/auth/UserAvatar";
import { OfflineIndicator } from "@/components/OfflineIndicator";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { setDebugFlag, useDebugFlag } from "@/hooks/debug-flags";

function DesktopNavTab({ to, label }: { to: string; label: string }) {
  const active = useMatch(to);
  return (
    <Link
      to={to}
      className={`rounded px-3 py-1 text-sm ${active ? "font-medium text-gray-900" : "text-gray-500 hover:text-gray-900"}`}>
      {label}
    </Link>
  );
}

function MobileBottomNavTab({
  to,
  label,
  Icon,
}: {
  to: string;
  label: string;
  Icon: NavTab["Icon"];
}) {
  const active = useMatch(to);
  return (
    <Link
      to={to}
      className={`flex flex-1 flex-col items-center justify-center gap-1 py-2 text-xs ${active ? "text-blue-600" : "text-gray-600 hover:text-gray-900"}`}>
      <Icon size={24} aria-hidden />
      {label}
    </Link>
  );
}

function UserMenuDesktop() {
  const mayShowDebug = useDebugFlag("showDebugOptions");
  const user = useUser();
  if (!user) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900">
        <UserAvatar user={user} />
        <span className="max-w-42 truncate">{user.name}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56 text-base">
        <DropdownMenuItem disabled>
          <span className="truncate text-xs">{user.email}</span>
        </DropdownMenuItem>
        <DropdownMenuItem className="text-sm/relaxed" asChild>
          <Link to="/settings">Settings</Link>
        </DropdownMenuItem>
        {mayShowDebug && (
          <DropdownMenuItem
            className="text-sm/relaxed"
            onClick={() => setDebugFlag("openDebugDialog", true)}>
            Debug
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem className="text-sm/relaxed" onClick={signOut}>
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Navbar() {
  const user = useUser();
  const navTabs = useNavTabs();

  return (
    <nav
      style={{ gridArea: "header" }}
      className="sticky top-0 z-10 flex items-center gap-3 border-b border-slate-200 bg-slate-50 px-4 py-2 text-sm">
      <div className="sm:hidden">
        <MenuSheet />
      </div>

      <Link className="hidden font-semibold sm:block" to="/">
        PlanTopo
      </Link>

      <div className="hidden items-center gap-1 sm:mr-auto sm:flex">
        {navTabs.map(tab => (
          <DesktopNavTab key={tab.to} to={tab.to} label={tab.label} />
        ))}
      </div>

      <OfflineIndicator className="mt-0.5" />

      <div className="hidden sm:block">
        <UserMenuDesktop />
      </div>

      {!user && !window.Native && (
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to="/login">Sign in</Link>
          </Button>
          <Button asChild>
            <Link to="/signup">Sign up</Link>
          </Button>
        </div>
      )}
    </nav>
  );
}

export function DesktopFooter() {
  const mayShowDebug = useDebugFlag("showDebugOptions");
  return (
    <footer
      style={{ gridArea: "footer" }}
      className="hidden bg-gray-100 px-4 py-2 text-xs text-gray-800 sm:flex">
      <div className="ml-auto flex gap-3">
        {FOOTER_LINKS.map((link, i) => (
          <FooterLinkComponent key={i} link={link} />
        ))}

        {mayShowDebug && (
          <span className="text-gray-500">
            <span>|</span>
            <FooterLinkComponent
              link={{
                onClick: () => setDebugFlag("openDebugDialog", true),
                label: "Debug",
              }}
            />
            <span>|</span>
          </span>
        )}
      </div>
    </footer>
  );
}

export function FooterLinkComponent({ link }: { link: FooterLink }) {
  return "to" in link ? (
    <Link to={link.to} className="hover-only-link">
      {link.label}
    </Link>
  ) : (
    <button onClick={link.onClick} className="hover-only-link">
      {link.label}
    </button>
  );
}

export function MobileBottomNav() {
  const navTabs = useMobileBottomNavTabs();
  return (
    <nav
      style={{ gridArea: "footer" }}
      className="sticky bottom-0 flex border-t border-gray-200 bg-white sm:hidden">
      {navTabs.map(tab => (
        <MobileBottomNavTab
          key={tab.to}
          to={tab.to}
          label={tab.label}
          Icon={tab.Icon}
        />
      ))}
    </nav>
  );
}
